import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api, { formatErr } from "../../lib/api";
import { toast } from "sonner";
import {
  Plus,
  Banknote,
  CreditCard,
  ShieldCheck,
  X,
} from "@/components/icons";
import Portal from "../../components/Portal";
import StatCard from "../../components/StatCard";

const METHOD_LABEL = {
  cash: "Efectivo",
  card: "Tarjeta",
  insurance: "Seguro",
  transfer: "Transferencia",
};
const METHOD_ICON = {
  cash: Banknote,
  card: CreditCard,
  insurance: ShieldCheck,
  transfer: CreditCard,
};

export default function Payments() {
  const location = useLocation();
  const navigate = useNavigate();
  const [pays, setPays] = useState([]);
  const [patients, setPatients] = useState([]);
  const [tab, setTab] = useState("pending");
  const [collectTarget, setCollectTarget] = useState(null); // payment being collected
  const [collectForm, setCollectForm] = useState({ amount: "", method: "cash" });
  const [newOpen, setNewOpen] = useState(false);
  const [newForm, setNewForm] = useState({
    patient_id: "",
    amount: "",
    method: "cash",
    concept: "",
  });

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get("/payments"),
      api.get("/patients"),
    ]);
    setPays(a.data);
    setPatients(b.data);
  };
  useEffect(() => {
    load();
  }, []);

  // Auto-open the collect modal if URL has ?collect=<id>
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("collect");
    if (!id || pays.length === 0) return;
    const pay = pays.find((p) => p.id === id);
    if (pay && pay.status === "pending") {
      openCollect(pay);
    }
    // clear query param so it doesn't re-trigger
    navigate("/secretaria/cobros", { replace: true });
  }, [pays, location.search]);

  const openCollect = (pay) => {
    setCollectTarget(pay);
    setCollectForm({
      amount: pay.amount && pay.amount > 0 ? String(pay.amount) : "",
      method: "cash",
    });
  };

  const confirmCollect = async (e) => {
    e.preventDefault();
    const amount = parseFloat(collectForm.amount);
    if (!amount || amount <= 0) {
      toast.error("Ingresa un monto válido");
      return;
    }
    try {
      await api.put(`/payments/${collectTarget.id}`, {
        amount,
        method: collectForm.method,
        status: "paid",
      });
      toast.success("Cobro registrado");
      setCollectTarget(null);
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const submitNew = async (e) => {
    e.preventDefault();
    try {
      await api.post("/payments", {
        ...newForm,
        amount: parseFloat(newForm.amount),
        status: "paid",
      });
      toast.success("Cobro registrado");
      setNewOpen(false);
      setNewForm({ patient_id: "", amount: "", method: "cash", concept: "" });
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const patientById = useMemo(
    () => Object.fromEntries(patients.map((p) => [p.id, p])),
    [patients],
  );

  const pending = pays.filter((p) => p.status === "pending");
  const paid = pays.filter((p) => (p.status || "paid") === "paid");

  const _now = new Date();
  const today = `${_now.getFullYear()}-${String(_now.getMonth() + 1).padStart(2, "0")}-${String(_now.getDate()).padStart(2, "0")}`;
  const todayTot = paid
    .filter((p) => (p.paid_at || p.created_at || "").slice(0, 10) === today)
    .reduce((a, p) => a + (p.amount || 0), 0);
  const totalPaid = paid.reduce((a, p) => a + (p.amount || 0), 0);

  const rows = tab === "pending" ? pending : paid;

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-black tracking-tight">
            Cobros a pacientes
          </h1>
          <p className="text-sm text-black/60">
            Registra los pagos recibidos por consultas y servicios.
          </p>
        </div>
        <button
          onClick={() => setNewOpen(true)}
          className="btn-primary inline-flex items-center gap-2"
          data-testid="new-payment-button"
        >
          <Plus className="w-4 h-4" /> Nuevo cobro
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-4 pt-2">
        <StatCard
          title="Pendientes"
          value={pending.length}
          subtitle="Por cobrar"
          active={pending.length > 0}
          testid="stat-pending"
        />
        <StatCard
          title="Cobrado hoy"
          value={`$${todayTot.toFixed(2)}`}
          subtitle="Ingresos del día"
          testid="stat-today"
        />
        <StatCard
          title="Total cobrado"
          value={`$${totalPaid.toFixed(2)}`}
          subtitle="Acumulado"
          testid="stat-total"
        />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-[#E5E7EB]">
        <TabBtn
          active={tab === "pending"}
          onClick={() => setTab("pending")}
          testid="tab-pending"
          count={pending.length}
        >
          Pendientes
        </TabBtn>
        <TabBtn
          active={tab === "paid"}
          onClick={() => setTab("paid")}
          testid="tab-paid"
          count={paid.length}
        >
          Cobrados
        </TabBtn>
      </div>

      <div className="glass rounded-[28px] overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="text-left">
            <tr className="text-[11px] uppercase tracking-widest text-black/55 border-b border-[#E5E7EB]">
              <th className="px-5 py-4">Fecha</th>
              <th className="px-5 py-4">Paciente</th>
              <th className="px-5 py-4">Concepto</th>
              {tab === "paid" && <th className="px-5 py-4">Método</th>}
              <th className="px-5 py-4 text-right">Monto</th>
              {tab === "pending" && <th className="px-5 py-4 text-right">Acción</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => {
              const Icon = METHOD_ICON[p.method] || CreditCard;
              const dateStr =
                tab === "paid" && p.paid_at
                  ? new Date(p.paid_at).toLocaleString("es-ES")
                  : new Date(p.created_at).toLocaleString("es-ES");
              return (
                <tr
                  key={p.id}
                  data-testid={`pay-row-${p.id}`}
                  className="border-b border-[#E5E7EB] hover:bg-black/[0.03]"
                >
                  <td className="px-5 py-3 text-black/80 whitespace-nowrap">
                    {dateStr}
                  </td>
                  <td className="px-5 py-3 font-bold text-black">
                    {patientById[p.patient_id]?.full_name || "—"}
                  </td>
                  <td className="px-5 py-3 text-black/80">{p.concept || "—"}</td>
                  {tab === "paid" && (
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/[0.04] text-black/80 text-xs font-bold">
                        <Icon className="w-3 h-3" />
                        {METHOD_LABEL[p.method] || p.method}
                      </span>
                    </td>
                  )}
                  <td className="px-5 py-3 text-right font-bold text-black">
                    {p.amount && p.amount > 0 ? `$${p.amount.toFixed(2)}` : "—"}
                  </td>
                  {tab === "pending" && (
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={() => openCollect(p)}
                        data-testid={`collect-btn-${p.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#01BC8E] text-white text-xs font-bold hover:bg-[#019A75] transition"
                      >
                        Cobrar
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td
                  colSpan={tab === "pending" ? 5 : 5}
                  className="text-center text-black/55 py-10"
                >
                  {tab === "pending"
                    ? "Sin pagos pendientes."
                    : "Sin cobros registrados."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Collect modal */}
      {collectTarget && (
        <Portal>
          <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 backdrop-blur-md p-4 overflow-y-auto">
            <form
              onSubmit={confirmCollect}
              className="bg-white rounded-3xl p-6 w-full max-w-md space-y-4 my-4 shadow-xl border border-[#E5E7EB]"
              data-testid="collect-modal"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-extrabold text-black">
                    Cobrar consulta
                  </h3>
                  <p className="text-xs text-black/60 mt-1">
                    {patientById[collectTarget.patient_id]?.full_name ||
                      "Paciente"}{" "}
                    · {collectTarget.concept || "Consulta"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setCollectTarget(null)}
                  className="p-1.5 rounded-full hover:bg-black/[0.06]"
                  data-testid="collect-close"
                >
                  <X className="w-4 h-4 text-black/60" />
                </button>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-widest font-bold text-black/55">
                  Cantidad
                </label>
                <div className="relative mt-1">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-black/50 font-bold">
                    $
                  </span>
                  <input
                    autoFocus
                    required
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="0.00"
                    value={collectForm.amount}
                    onChange={(e) =>
                      setCollectForm({ ...collectForm, amount: e.target.value })
                    }
                    className="w-full bg-white border border-[#E5E7EB] rounded-xl pl-8 pr-4 py-3 text-lg font-bold text-black focus:outline-none focus:border-black/40"
                    data-testid="collect-amount"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-widest font-bold text-black/55">
                  Método de pago
                </label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {[
                    { key: "cash", label: "Efectivo", Icon: Banknote },
                    { key: "card", label: "Tarjeta", Icon: CreditCard },
                    { key: "insurance", label: "Seguro", Icon: ShieldCheck },
                  ].map(({ key, label, Icon }) => {
                    const active = collectForm.method === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() =>
                          setCollectForm({ ...collectForm, method: key })
                        }
                        data-testid={`collect-method-${key}`}
                        className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border text-xs font-bold transition ${
                          active
                            ? "border-black bg-black text-white"
                            : "border-[#E5E7EB] bg-white text-black hover:border-black/40"
                        }`}
                      >
                        <Icon className="w-5 h-5" strokeWidth={1.8} />
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setCollectTarget(null)}
                  className="px-5 py-2 rounded-full font-bold text-black"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  data-testid="collect-confirm"
                >
                  Confirmar cobro
                </button>
              </div>
            </form>
          </div>
        </Portal>
      )}

      {/* New manual payment modal */}
      {newOpen && (
        <Portal>
          <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 backdrop-blur-md p-4 overflow-y-auto">
            <form
              onSubmit={submitNew}
              className="bg-white rounded-3xl p-6 w-full max-w-md space-y-3 my-4 shadow-xl border border-[#E5E7EB]"
              data-testid="new-payment-modal"
            >
              <h3 className="text-lg font-extrabold text-black">Nuevo cobro</h3>
              <select
                required
                value={newForm.patient_id}
                onChange={(e) =>
                  setNewForm({ ...newForm, patient_id: e.target.value })
                }
                className="w-full bg-white border border-[#E5E7EB] rounded-xl px-4 py-2.5 text-black"
                data-testid="pay-patient-select"
              >
                <option value="">Paciente</option>
                {patients.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.full_name}
                  </option>
                ))}
              </select>
              <input
                required
                type="number"
                step="0.01"
                min="0"
                placeholder="Monto"
                value={newForm.amount}
                onChange={(e) =>
                  setNewForm({ ...newForm, amount: e.target.value })
                }
                className="w-full bg-white border border-[#E5E7EB] rounded-xl px-4 py-2.5 text-black"
                data-testid="pay-amount-input"
              />
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: "cash", label: "Efectivo", Icon: Banknote },
                  { key: "card", label: "Tarjeta", Icon: CreditCard },
                  { key: "insurance", label: "Seguro", Icon: ShieldCheck },
                ].map(({ key, label, Icon }) => {
                  const active = newForm.method === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setNewForm({ ...newForm, method: key })}
                      data-testid={`new-method-${key}`}
                      className={`flex flex-col items-center gap-1.5 py-2.5 rounded-xl border text-xs font-bold transition ${
                        active
                          ? "border-black bg-black text-white"
                          : "border-[#E5E7EB] bg-white text-black hover:border-black/40"
                      }`}
                    >
                      <Icon className="w-4 h-4" strokeWidth={1.8} />
                      {label}
                    </button>
                  );
                })}
              </div>
              <input
                placeholder="Concepto (consulta, examen…)"
                value={newForm.concept}
                onChange={(e) =>
                  setNewForm({ ...newForm, concept: e.target.value })
                }
                className="w-full bg-white border border-[#E5E7EB] rounded-xl px-4 py-2.5 text-black"
              />
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setNewOpen(false)}
                  className="px-5 py-2 rounded-full font-bold text-black"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  data-testid="pay-submit-button"
                >
                  Registrar
                </button>
              </div>
            </form>
          </div>
        </Portal>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid, count }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`relative px-4 py-3 text-sm font-bold transition ${
        active ? "text-black" : "text-black/55 hover:text-black"
      }`}
    >
      {children}
      {typeof count === "number" && (
        <span
          className={`ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] rounded-full ${
            active ? "bg-black text-white" : "bg-black/[0.06] text-black/70"
          }`}
        >
          {count}
        </span>
      )}
      {active && (
        <span className="absolute left-0 right-0 bottom-[-1px] h-[2px] bg-black" />
      )}
    </button>
  );
}
